import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { isPricingLive } from "@/config/pricing";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { ArrowUp, ArrowDown, Minus, Bookmark, Check, Lock, ExternalLink, ChevronDown } from "lucide-react";

const DEMO_SLUGS = ["university-of-melbourne", "university-of-sydney"];

export default function Compare() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState({ results: [], insights: [] });
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const [showUpgradeHint, setShowUpgradeHint] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(true);
  const isLocked = isPricingLive() && !user?.is_paid;

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    (async () => {
      const { data: u } = await api.get("/universities");
      setAll(u);
      if (!user) {
        setSelected(DEMO_SLUGS);
        return;
      }
      const slugsParam = params.get("slugs");
      const initial = params.get("slug");
      if (slugsParam) {
        setSelected(slugsParam.split(",").filter(Boolean).slice(0, 4));
      } else if (initial) {
        setSelected([initial]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selected.length === 0) { setData({ results: [], insights: [] }); return; }
    (async () => {
      const { data } = await api.post("/compare", { slugs: selected });
      setData(data);
    })();
  }, [selected]);

  const toggle = (slug) => {
    if (!user) { startGoogle(); return; }
    setSelected(s => {
      if (s.includes(slug)) return s.filter(x => x !== slug);
      // Free limit: 2. Paid limit: 4.
      const limit = isLocked ? 2 : 4;
      if (s.length >= limit) {
        if (isLocked) setShowUpgradeHint(true);
        return s;
      }
      setShowUpgradeHint(false);
      return [...s, slug];
    });
  };

  const handleSave = async () => {
    if (!user || selected.length < 2) return;
    setSaving(true);
    try {
      await api.post("/saved-comparisons", { slugs: selected });
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 2200);
    } finally {
      setSaving(false);
    }
  };

  // Country-specific Aug-intake application window (start – end month for international undergrads)
  const APPLY_WINDOW = {
    AU: "Sep – Dec", UK: "Sep – Jan", US: "Aug – Jan", DE: "Apr – Jul",
    NL: "Oct – May", CA: "Sep – Mar", IE: "Nov – Feb", SG: "Oct – Mar",
    FR: "Nov – Mar", SE: "Oct – Jan", NZ: "Aug – Dec",
  };
  const applyWindow = (u) => APPLY_WINDOW[u.country_code] || "Aug – Jan";

  // Country-specific authoritative source URLs for the few rows where the source
  // varies by country (post-study work visa, application timeline, etc.).
  const VISA_URL = {
    AU: "https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/temporary-graduate-485",
    UK: "https://www.gov.uk/graduate-visa",
    US: "https://www.ice.gov/sevis/practical-training",
    DE: "https://www.make-it-in-germany.com/en/visa-residence/types/job-seeking",
    NL: "https://ind.nl/en/residence-permits/study/orientation-year-permit",
    CA: "https://www.canada.ca/en/immigration-refugees-citizenship/services/study-canada/work/after-graduation.html",
    IE: "https://www.irishimmigration.ie/coming-to-study-in-ireland/third-level-graduate-programme/",
    SG: "https://www.mom.gov.sg/passes-and-permits",
    FR: "https://www.campusfrance.org/en/post-study-work-visa-france",
    SE: "https://www.migrationsverket.se/English/Private-individuals/Studying-and-researching-in-Sweden",
    NZ: "https://www.immigration.govt.nz/new-zealand-visas/visas/visa/post-study-work-visa",
  };
  const APPLY_URL = {
    AU: "https://www.studyaustralia.gov.au/en/plan-your-studies/how-to-apply",
    UK: "https://www.ucas.com/undergraduate/applying-university/ucas-undergraduate-when-apply",
    US: "https://bigfuture.collegeboard.org/plan-for-college/applying-to-college/timeline",
    DE: "https://www.daad.de/en/studying-in-germany/application/",
    NL: "https://www.studyinnl.org/finding-a-study/how-to-apply",
    CA: "https://www.educanada.ca/study-plan-etudes/before-avant/applying-application.aspx",
    IE: "https://www.educationinireland.com/en/how-do-i-apply-",
    SG: "https://www.studyinsg.com/applying",
    FR: "https://www.campusfrance.org/en/how-to-apply-to-french-higher-education",
    SE: "https://www.universityadmissions.se/en/all-you-need-to-know/applying-for-studies/",
    NZ: "https://www.studywithnewzealand.govt.nz/en/how-to-apply",
  };

  // Returns the authoritative source URL for a given row label and university.
  // Most rows come from the uni's own website (attached as `field_meta[k].url` by the backend);
  // visa/timeline rows are country-policy and use curated per-country URLs.
  const sourceUrl = (label, u) => {
    const m = u.field_meta || {};
    switch (label) {
      case "QS Rank 2025":   return m.qs_rank_2025?.url || null;
      case "IB range":       return m.ib_score_range_low?.url || null;
      case "SAT range":      return m.sat_score_range_low?.url || null;
      case "IELTS range":    return m.ielts_score_range_low?.url || null;
      case "Acceptance":     return m.acceptance_rate_pct?.url || null;
      case "Appl Timeline":  return APPLY_URL[u.country_code] || null;
      case "Total / year":   return m.tuition_per_year_inr?.url || null;
      case "Employment":     return m.employment_rate_pct?.url || null;
      case "Post-study work": return VISA_URL[u.country_code] || null;
      case "Scholarships":   return m.scholarships_available?.url || null;
      default: return null;
    }
  };

  // Short label shown in the tooltip — pulled from field_meta when available, else a sensible default per row.
  const sourceLabel = (label, u) => {
    const m = u.field_meta || {};
    const map = {
      "QS Rank 2025": m.qs_rank_2025?.source,
      "IB range": m.ib_score_range_low?.source,
      "SAT range": m.sat_score_range_low?.source,
      "IELTS range": m.ielts_score_range_low?.source,
      "Acceptance": m.acceptance_rate_pct?.source,
      "Appl Timeline": "Official study-abroad portal",
      "Total / year": m.tuition_per_year_inr?.source,
      "Employment": m.employment_rate_pct?.source,
      "Post-study work": m.post_study_work_years?.source,
      "Scholarships": m.scholarships_available?.source,
    };
    return map[label] || "Source";
  };

  // Range of likely scholarship coverage % based on the university's scholarship availability and selectivity.
  // (Profile-aware: stronger IB profile → upper end of range.)
  const scholarshipRange = (u) => {
    if (!u.scholarships_available) return "0–10%";
    if ((u.ib_score_median || 0) >= 39) return "25–45%";
    if ((u.ib_score_median || 0) >= 36) return "20–35%";
    return "15–30%";
  };
  const scholarshipRank = (u) => {
    if (!u.scholarships_available) return 5;
    if ((u.ib_score_median || 0) >= 39) return 35;
    if ((u.ib_score_median || 0) >= 36) return 28;
    return 22;
  };

  // [label, value-formatter, raw-number-or-null for trend, lowerIsBetter?, emph?]
  const rows = [
    ["Country",           u => u.country,                                                       null,                                            null,  false],
    ["QS Rank 2025",      u => `#${u.qs_rank_2025}`,                                            u => u.qs_rank_2025,                             true,  false],
    ["IB range",          u => `${u.ib_score_range_low}–${u.ib_score_range_high} / 45`,         u => u.ib_score_range_low,                       true,  false],
    ["SAT range",         u => `${u.sat_score_range_low}–${u.sat_score_range_high}`,            u => u.sat_score_range_low,                      true,  false],
    ["IELTS range",       u => `${u.ielts_score_range_low}–${u.ielts_score_range_high}`,        u => u.ielts_score_range_low,                    true,  false],
    ["Acceptance",        u => `${u.acceptance_rate_pct}%`,                                     u => u.acceptance_rate_pct,                      false, false],
    ["Appl Timeline",     u => applyWindow(u),                                                  null,                                            null,  false],
    ["Total / year",      u => fmtINR(u.tuition_per_year_inr + u.living_per_year_inr),          u => u.tuition_per_year_inr + u.living_per_year_inr, true, true],
    ["Employment",        u => `${u.employment_rate_pct}%`,                                     u => u.employment_rate_pct,                      false, false],
    ["Post-study work",   u => `${u.post_study_work_years} yr`,                                 u => u.post_study_work_years,                    false, false],
    ["Scholarships",      u => scholarshipRange(u),                                             u => scholarshipRank(u),                         false, false],
  ];

  // For each row, compute best/worst across selected unis
  const trendsByRow = rows.map(([, , numFn, lowerIsBetter]) => {
    if (!numFn || !data.results.length) return null;
    const values = data.results.map(numFn);
    const best = lowerIsBetter ? Math.min(...values) : Math.max(...values);
    const worst = lowerIsBetter ? Math.max(...values) : Math.min(...values);
    return { values, best, worst, lowerIsBetter };
  });

  const Trend = ({ trend, value }) => {
    if (!trend || trend.best === trend.worst) return null;
    if (value === trend.best) {
      return <ArrowUp size={13} strokeWidth={2.4} className="text-[var(--success)] inline-block ml-1 -mt-0.5" />;
    }
    if (value === trend.worst) {
      return <ArrowDown size={13} strokeWidth={2.4} className="text-[var(--warning)] inline-block ml-1 -mt-0.5" />;
    }
    return <Minus size={12} strokeWidth={2.4} className="text-[var(--s300)] inline-block ml-1 -mt-0.5" />;
  };

  return (
    <div className="min-h-screen bg-white lg:pl-[240px]">
      <Nav />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-micro font-semibold uppercase tracking-[0.05em] text-[var(--s500)]">Decision Lab</span>
            </div>
            <h1 className="mt-3 text-h1 text-[var(--s900)]" data-testid="cmp-headline">
              Four universities, one decision, <em className="italic text-[var(--ind)]">side by side.</em>
            </h1>
          </div>
          {user && selected.length >= 2 && (
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="cmp-save"
              className={`inline-flex items-center gap-2 text-body-sm font-medium px-4 py-2.5 rounded-[var(--r8)] border transition-colors ${
                savedTick
                  ? "bg-emerald-50 text-[var(--success)] border-emerald-300"
                  : "bg-white text-[var(--ind)] border-[var(--ind)] hover:bg-[var(--ind-soft)]"
              }`}
            >
              {savedTick ? <Check size={15} strokeWidth={2.4} /> : <Bookmark size={15} strokeWidth={2} />}
              {savedTick ? "Saved to dashboard" : (saving ? "Saving…" : "Save this comparison")}
            </button>
          )}
        </div>
        <p className="text-body text-[var(--s600)] mt-3 mb-8 max-w-xl leading-relaxed">
          Choose up to four universities. We&apos;ll surface the trade-offs in plain English.
        </p>

        {/* Picker (accordion) */}
        <div className="card mb-6" data-testid="cmp-picker">
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            aria-expanded={pickerOpen}
            data-testid="cmp-picker-toggle"
            className="w-full flex items-center justify-between p-6 text-left"
          >
            <span className="text-body-sm font-semibold text-[var(--s700)]">
              Pick up to {isLocked ? 2 : 4}
            </span>
            <span className="flex items-center gap-3">
              <span
                className={`text-micro font-semibold tracking-[0.05em] uppercase px-2.5 py-1 rounded-[var(--r8)] ${
                  selected.length === (isLocked ? 2 : 4) ? "bg-[var(--ind)] text-white" : "bg-[var(--s100)] text-[var(--s600)]"
                }`}
                data-testid="cmp-count"
              >
                {selected.length} / {isLocked ? 2 : 4}
              </span>
              <ChevronDown
                size={18}
                strokeWidth={2}
                className="text-[var(--s500)] transition-transform"
                style={{ transform: pickerOpen ? "rotate(180deg)" : "rotate(0)" }}
              />
            </span>
          </button>
          {pickerOpen && (
            <div className="px-6 pb-6">
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-2">
                {all.map(u => {
                  const on = selected.includes(u.slug);
                  const lockedForGuest = !user;
                  const limit = isLocked ? 2 : 4;
                  const atHardLimit = !on && !isLocked && selected.length >= limit;
                  const disabled = lockedForGuest || atHardLimit;
                  return (
                    <button
                      key={u.slug}
                      onClick={() => toggle(u.slug)}
                      disabled={disabled}
                      title={lockedForGuest ? "Sign in to compare any universities" : undefined}
                      data-testid={`cmp-pick-${u.slug}`}
                      className={`text-body-sm px-3 py-1.5 rounded-[var(--r8)] border transition-colors ${
                        on
                          ? "bg-[var(--ind)] text-white border-[var(--ind)]"
                          : disabled
                            ? "bg-white text-[var(--s400)] border-[var(--s200)] cursor-not-allowed"
                            : "bg-white text-[var(--s700)] border-[var(--s200)] hover:border-[var(--s400)]"
                      }`}
                    >
                      {u.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Upgrade hint — only when user hits the locked limit */}
        {showUpgradeHint && (
          <div
            data-testid="cmp-upgrade-hint"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 14px",
              background: "var(--ind-soft)",
              borderRadius: "8px",
              marginTop: "-12px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "var(--s700)",
            }}
          >
            <span>Compare up to 4 universities with the Decide plan.</span>
            <button
              type="button"
              onClick={() => navigate("/pricing")}
              style={{
                color: "var(--ind)",
                fontWeight: 600,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                fontSize: "13px",
              }}
            >
              Upgrade →
            </button>
          </div>
        )}

        {/* Results (insights + table) — blurred for non-logged-in users */}
        {data.results?.length > 0 && (
          <div className="relative">
            <div className={!user ? "blur-[6px] select-none pointer-events-none" : ""}>
              {/* Insights */}
              {data.insights?.length > 0 && (
                <div className="rounded-[var(--r12)] border border-[var(--ind)] bg-[var(--ind-soft)] p-6 mb-6">
                  <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)] mb-3">In plain English</div>
                  <div className="space-y-3">
                    {data.insights.map((i, idx) => (
                      <div key={idx} className="flex gap-3 text-body text-[var(--s800)] leading-relaxed">
                        <span className="text-[var(--ind)]">—</span>
                        <span>{i}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Table */}
              {(() => {
                // Light tint palette for each university column (indigo, mint, violet, amber)
                const COL_TINTS = ["#EEF2FF", "#ECFDF5", "#F5F3FF", "#FFFBEB"];
                return (
              <div className="card overflow-x-auto">
                <table className="w-full text-body-sm" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr className="border-b border-[var(--s200)]">
                      <th className="text-left p-5 text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] w-44 bg-[var(--bg)]"></th>
                      {data.results.map((u, ci) => (
                        <th key={u.slug} className="text-left p-5" style={{ background: COL_TINTS[ci % COL_TINTS.length] }}>
                          <div className="text-body-lg font-semibold text-[var(--s900)] leading-snug">{u.name}</div>
                          <div className="text-micro text-[var(--s500)] mt-1">{u.city}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(([label, fn, numFn, lowerIsBetter, emph], i) => {
                      const trend = trendsByRow[i];
                      const isTotalYr = label === "Total / year";
                      return (
                        <tr key={i} className="border-b border-[var(--s200)]">
                          <td
                            className={`p-5 text-micro font-semibold tracking-[0.05em] uppercase ${isTotalYr ? "text-white" : "text-[var(--s500)]"}`}
                            style={isTotalYr ? { background: "var(--ind)" } : undefined}
                          >
                            {label}
                          </td>
                          {data.results.map((u, ci) => {
                            const rawVal = numFn ? numFn(u) : null;
                            const url = sourceUrl(label, u);
                            const valueNode = fn(u);
                            const cellBg = isTotalYr
                              ? "var(--ind-soft)"
                              : COL_TINTS[ci % COL_TINTS.length];
                            return (
                              <td
                                key={u.slug}
                                data-testid={`cmp-${u.slug}-${i}`}
                                className={`p-5 ${emph || isTotalYr ? "font-semibold text-[var(--s900)]" : "text-[var(--s800)]"}`}
                                style={{ background: cellBg }}
                              >
                                {url ? (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`Source: ${sourceLabel(label, u)}`}
                                    data-testid={`cmp-src-${u.slug}-${i}`}
                                    className="inline-flex items-center gap-1 underline decoration-dotted decoration-[var(--s300)] underline-offset-[3px] hover:decoration-[var(--ind)] hover:text-[var(--ind)] transition-colors"
                                  >
                                    <span>{valueNode}</span>
                                    <ExternalLink size={11} strokeWidth={2} className="text-[var(--s400)] group-hover:text-[var(--ind)]" />
                                  </a>
                                ) : (
                                  valueNode
                                )}
                                {numFn && <Trend trend={trend} value={rawVal} />}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    <tr>
                      <td className="p-5 align-top text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">Trade-offs</td>
                      {data.results.map((u, ci) => (
                        <td key={u.slug} className="p-5 align-top" style={{ background: COL_TINTS[ci % COL_TINTS.length] }}>
                          {(u.tradeoffs || []).map((t, i) => (
                            <div
                              key={i}
                              className="inline-block text-label px-2.5 py-1 rounded-[var(--r8)] bg-white text-[var(--s700)] border border-[var(--s200)] mr-1.5 mb-1.5"
                            >
                              {t}
                            </div>
                          ))}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
                );
              })()}
            </div>

            {!user && (
              <div className="absolute inset-0 flex items-start justify-center pt-20 px-4">
                <div className="text-center max-w-md bg-white/85 backdrop-blur-md border border-[var(--s200)] rounded-[var(--r20)] shadow-xl p-8">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[var(--ind-soft)] text-[var(--ind)] flex items-center justify-center mb-4">
                    <Lock size={20} strokeWidth={1.8} />
                  </div>
                  <h3 className="text-h2 font-semibold text-[var(--s900)] leading-tight">
                    Sign in to reveal the full comparison
                  </h3>
                  <p className="text-body-sm text-[var(--s600)] mt-2 leading-relaxed">
                    Side-by-side tuition, living, employment, post-study work and AI-surfaced trade-offs — for any four universities.
                  </p>
                  <button
                    onClick={startGoogle}
                    data-testid="cmp-unlock"
                    className="btn-primary mt-5 inline-flex items-center justify-center gap-2"
                  >
                    Get Early Access
                  </button>
                  <div className="mt-4 text-micro text-[var(--s500)] space-y-1">
                    <div><span className="text-[var(--success)] font-semibold">✓</span> Sign in securely with Google</div>
                    <div><span className="text-[var(--success)] font-semibold">✓</span> Free until October 30</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {selected.length === 0 && (
          <div className="text-center text-[var(--s500)] py-20">
            <div className="text-h3">Pick universities above to start comparing.</div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
